/**
 * テーブル用の並べ替え共通フック
 *
 * 使い方：
 *   const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort(rows, {
 *     defaultKey: 'menu_code',
 *     defaultDir: 'asc',
 *     accessors: { menu_code: (r) => Number(r.menu_code) || r.menu_code, ... }
 *   });
 *   <SortableTh sortKey="menu_code" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>コード</SortableTh>
 */
import { useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export type UseTableSortOptions<T> = {
  defaultKey?: string;
  defaultDir?: SortDir;
  /** 各カラムの値取得。省略時は row[key] を使用 */
  accessors?: Record<string, (row: T) => unknown>;
};

export function useTableSort<T extends Record<string, unknown>>(
  rows: T[],
  options: UseTableSortOptions<T> = {},
) {
  const [sortKey, setSortKey] = useState<string | null>(options.defaultKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(options.defaultDir ?? 'asc');

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      // 同じキーを押した場合：asc → desc → 解除
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortKey(null); setSortDir('asc'); }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const accessor = options.accessors?.[sortKey] ?? ((r: T) => r[sortKey]);
    const list = [...rows];
    list.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // null/undefined を末尾へ
      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else if (typeof av === 'boolean' && typeof bv === 'boolean') cmp = (av === bv) ? 0 : (av ? -1 : 1);
      else cmp = String(av).localeCompare(String(bv), 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  return { sortKey, sortDir, toggleSort, sortedRows };
}
